import React, { useCallback, useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown,
  faCalendarAlt,
  faFilter,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
// import ToolTips from "../ToolTips";
import { MapModal } from "../Modal";

const Categories = ({
  handleChange,
  formData,
  handleFilter,
  lat,
  lng,
  onRadioChange,
  state,
  onScrollToEmpty,
  showEmptyBorders = false,
  catEdad,
}) => {
  const [radius, setRadius] = useState(2000);
  const [showMapModal, setShowMapModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Estados temporales para el modal del mapa (no afectan el filtrado hasta guardar)
  const [tempLat, setTempLat] = useState(lat);
  const [tempLng, setTempLng] = useState(lng);
  const [tempRadius, setTempRadius] = useState(radius);

  // Estado para mostrar información de ubicación seleccionada
  const [selectedLocationInfo, setSelectedLocationInfo] = useState(null);

  const petFormDataState = useSelector((state) => state.compartirMascota);

  // Función para obtener información de ubicación usando geocodificación inversa
  const getLocationInfo = async (latitude, longitude) => {
    try {
      // Usar Nominatim (OpenStreetMap) que es gratuito y no requiere API key
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=es&addressdetails=1`
      );

      if (response.ok) {
        const data = await response.json();
        if (data && data.address) {
          const address = data.address;

          // Extraer información relevante en orden de prioridad
          const city =
            address.city ||
            address.town ||
            address.village ||
            address.municipality ||
            address.suburb;
          const state = address.state || address.province;
          const country = address.country;

          // Construir nombre de ubicación
          if (city && state) {
            return `${city}, ${state}`;
          } else if (city) {
            return `${city}, ${country}`;
          } else if (state) {
            return `${state}, ${country}`;
          } else if (country) {
            return country;
          } else {
            return "Ubicación seleccionada";
          }
        }
      }
    } catch (error) {
      console.warn("Error obteniendo información de ubicación:", error);
    }

    // Fallback: mostrar coordenadas aproximadas
    return `Lat: ${latitude.toFixed(4)}, Lng: ${longitude.toFixed(4)}`;
  };

  // Sincronizar estados temporales cuando cambian los valores reales
  useEffect(() => {
    setTempLat(lat);
    setTempLng(lng);
    setTempRadius(radius);
  }, [lat, lng, radius]);

  const edadCategories = [
    { min: 0, max: 4, cat: "Cachorro" },
    { min: 5, max: 8, cat: "Joven" },
    { min: 9, max: 16, cat: "Anciano" },
  ];

  // Nueva función para cambios temporales del radio en el modal
  const handleTempRadiusChange = (event) => {
    const newRadius = parseInt(event?.target?.value);
    if (!isNaN(newRadius)) {
      setTempRadius(newRadius);
    }
  };

  // FUNCIÓN PROBLEMÁTICA ORIGINAL - Causaba filtrado automático
  // const handleMarkerAdded = useCallback(
  //   (newLat, newLng) => {
  //     onRadioChange(radius, newLat, newLng);  // ← PROBLEMA: Actualizaba inmediatamente
  //     handleFilter();                        // ← PROBLEMA: Filtraba inmediatamente
  //   },
  //   [radius, onRadioChange, handleFilter]
  // );

  // Nueva función para actualización temporal del marcador (SIN filtrar)
  const handleTempMarkerAdded = useCallback((newLat, newLng) => {
    setTempLat(newLat);
    setTempLng(newLng);
    // NO ejecuta handleFilter() - solo actualiza estados temporales
  }, []);

  const handleCheckboxChange = (event, category) => {
    const { value, checked } = event.target;

    if (checked) {
      handleChange({ target: { name: category, value } });
    } else {
      handleChange({ target: { name: category, value: "" } });
    }
  };

  const tipoRef = useRef(null);
  const scrollToTipoFilter = () => {
    tipoRef.current.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  // Está para pasar el funcionamiento a Buscador
  useEffect(() => {
    if (onScrollToEmpty) {
      onScrollToEmpty(scrollToTipoFilter);
    }
  }, [onScrollToEmpty]);

  // Aplica borde naranja a los elementos vacíos
  const getBorderClass = (value, type = "form") => {
    if (!showEmptyBorders) return type === "form" ? "" : "";
    if (!value)
      return (
        "border-2 border-orange-500" +
        (type === "label" ? " rounded-lg p-2" : "")
      );
    return type === "form" ? "border-2 border-gray-200" : "";
  };

  return (
    <>
      {/* Botón móvil para abrir filtros */}
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="fixed bottom-4 left-4 z-40 bg-orange-500 text-white p-4 rounded-full shadow-lg md:hidden hover:bg-orange-600 transition-all duration-300"
      >
        <FontAwesomeIcon icon={faFilter} className="text-2xl" />
      </button>

      {/* Overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      <div
        className={`
        ${isMenuOpen ? "translate-y-0" : "translate-y-full"} 
        fixed bottom-0 left-0 right-0 
        bg-white rounded-t-3xl shadow-xl
        transform transition-transform duration-150 ease-in-out
        p-5 z-50
        overflow-y-auto max-h-[80vh]
        md:transform-none md:static md:rounded-none md:shadow-none md:p-0
        md:max-h-none md:overflow-visible
        ${isMenuOpen ? "pointer-events-auto" : ""}
      `}
      >
        <section className="flex flex-col text-start gap-6 items-center w-full max-w-md mx-auto">
          <div className="flex items-center justify-center gap-2">
            <label className="font-main font-bold text-xl uppercase">
              Filtros de busqueda
            </label>
          </div>

          {/* Ubicación */}
          <div className="h-full w-full">
            {/* <ToolTips text="Marca el lugar y selecciona el radio del área"> */}
              <div
                className={`z-50 flex flex-col justify-center items-center text-center h-full w-full font-main font-medium text-lg ${getBorderClass(
                  formData.lat && formData.lng,
                  "label"
                )}`}
              >
                <button
                  onClick={() => {
                    if (selectedLocationInfo) {
                      // Eliminar filtros de ubicación
                      setSelectedLocationInfo(null);
                      setRadius(10000); // Reset radius to default
                      setTempLat(null);
                      setTempLng(null);
                      setTempRadius(10000);
                      // Usar onRadioChange para limpiar lat/lng del formData
                      onRadioChange(10000, null, null);
                      // Ejecutar filtro sin ubicación
                      handleFilter();
                    } else {
                      // Abrir modal para seleccionar ubicación
                      setShowMapModal(true);
                    }
                  }}
                  className={`h-[60px] min-w-[180px] w-[8dvw] ${
                    selectedLocationInfo
                      ? "bg-gray-300 hover:bg-orange-500 hover:text-white text-gray-700"
                      : "bg-secondary hover:bg-orange-400 text-white"
                  } font-bold py-3 px-3 rounded-lg flex items-center justify-center gap-2 mb-2 transition-colors duration-200`}
                >
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  {selectedLocationInfo ? "Eliminar filtro" : "Ubicación"}
                </button>

                {/* Texto dinámico que se actualiza según el estado */}
                <div className="text-sm text-gray-600 font-main px-2">
                  {selectedLocationInfo ? (
                    <div className="text-green-700">
                      <div className="font-semibold">
                        {selectedLocationInfo}
                      </div>
                      <div className="text-xs">Radio: {radius / 1000}km</div>
                    </div>
                  ) : (
                    <div className="text-gray-600 font-main font-medium">
                      Selecciona un rango de búsqueda
                    </div>
                  )}
                </div>
              </div>
            {/* </ToolTips> */}
          </div>

          {/* Fecha perdido */}

          {state !== 4 ? (
            <div className="text-center">
              <label className="font-main font-semibold text-md uppercase">
                Fecha de perdido
              </label>
              {/* <ToolTips text="Marcar la fecha en la que perdió la mascota"> */}
                <form
                  className="flex items-center justify-center relative h-[60px] min-w-[228px] w-[10dvw]  font-main"
                  onClick={() =>
                    document.getElementById("custom-date").showPicker()
                  }
                >
                  <input
                    id="custom-date"
                    type="date"
                    name="fecha_perdido"
                    value={formData.fecha_perdido}
                    onChange={handleChange}
                    className={`custom-date-input font-main font-medium h-full w-full p-2 pl-4 pr-10 appearance-none bg-white $${getBorderClass(
                      formData.fecha_perdido
                    )} rounded-lg hover:border-gray-400 focus:ring-1 focus:ring-[#504527] transition-all duration-200 cursor-pointer shadow-sm`}
                  />
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                  />
                </form>
              {/* </ToolTips> */}
            </div>
          ) : (
            <></>
          )}

          <label className="font-main font-semibold text-md uppercase">
            Describenos a tu amigo!
          </label>

          {/* Tipo de amigo */}
          <div ref={tipoRef} className="text-center items-center">
            <form className="flex items-center justify-center font-main min-w-[228px] w-[10dvw]">
              <div className="flex gap-2 pb-0">
                {petFormDataState.tipo.map((option, index) => (
                  <div key={index} className="relative">
                    <input
                      type="checkbox"
                      id={`tipo-${option.id}`}
                      name="tipo"
                      value={option.id}
                      checked={formData.tipo === option.id}
                      onChange={(event) => handleCheckboxChange(event, "tipo")}
                      className="hidden peer"
                    />
                    <label
                      htmlFor={`tipo-${option.id}`}
                      className={`flex items-center font-main font-medium h-[55px] w-[90px] cursor-pointer rounded-lg p-4 justify-center bg-white shadow-md hover:bg-gray-300 peer-checked:bg-secondary peer-checked:text-white ${getBorderClass(
                        formData.tipo
                      )}`}
                    >
                      {option.nombre}
                    </label>
                  </div>
                ))}
              </div>
            </form>
          </div>

          {/* Género */}
          <div className="text-center flex flex-col items-center">
            <label className="font-main font-semibold text-md uppercase">
              Sexo
            </label>
            <form className="flex items-center justify-center font-main pt-2 min-w-[228px] w-[10dvw]">
              <div className="flex gap-2">
                {petFormDataState.sexo.map((option, index) => (
                  <div key={index} className="relative">
                    <input
                      type="checkbox"
                      name="sexo"
                      id={`sexo-${option.id}`}
                      value={option.id}
                      checked={formData.sexo === option.id}
                      onChange={(event) => handleCheckboxChange(event, "sexo")}
                      className="hidden peer "
                    />
                    <label
                      htmlFor={`sexo-${option.id}`}
                      className={`flex items-center font-main font-medium h-[55px] w-[90px] cursor-pointer rounded-lg p-4 justify-center bg-white shadow-md hover:bg-gray-300 peer-checked:bg-secondary peer-checked:text-white ${getBorderClass(
                        formData.sexo
                      )}`}
                    >
                      {option.nombre}
                    </label>
                  </div>
                ))}
              </div>
            </form>
          </div>

          {/* Raza */}

          <div className="text-center">
            <label className="font-main font-semibold uppercase block mb-2">
              Raza
            </label>
            {/* <ToolTips text="En caso de no saber dejar marcado ¨Seleccionar raza¨"> */}
              <form className="flex items-center justify-center relative h-[60px] min-w-[228px] w-[10dvw] font-medium font-main">
                <select
                  name="raza"
                  value={formData.raza}
                  onChange={handleChange}
                  className={`font-main font-medium h-full w-full p-2 pl-4 pr-8 appearance-none bg-white ${getBorderClass(
                    formData.raza
                  )} rounded-lg hover:border-gray-400 focus:ring-1 focus:ring-[#504527] transition-all duration-200 cursor-pointer text-gray-700 shadow-sm`}
                >
                  <option value="" className="font-main font-medium border-b-2">
                    Seleccionar raza
                  </option>
                  {petFormDataState.raza.map((option, index) => (
                    <option
                      key={index}
                      value={option.id}
                      className="font-main text-medium text-gray-400"
                    >
                      {option.nombre}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <FontAwesomeIcon
                    icon={faAngleDown}
                    className="h-[2dvh] text-gray-600"
                  />
                </div>
              </form>
            {/* </ToolTips> */}
          </div>

          {/* Color */}

          <div className="text-center">
            <label className="font-main font-semibold uppercase">Color</label>
            {/* <ToolTips text="Si ninguna opcion es especifica marcar la mas aproximada"> */}
              <form className="flex item-center justify-center relative h-[60px] min-w-[228px] w-[10dvw] font-main font-medium">
                <select
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  className={`font-main font-main h-full w-full p-2 pl-4 pr-8 appearance-none bg-white ${getBorderClass(
                    formData.color
                  )} rounded-lg hover:border-gray-400 focus:ring-1 focus:ring-[#504527] transition-all duration-200 cursor-pointer text-gray-700 shadow-sm`}
                >
                  <option value="" className="font-main font-medium border-b-2">
                    Seleccionar color
                  </option>
                  {petFormDataState.color.map((option, index) => (
                    <option
                      key={index}
                      value={option.id}
                      className="font-main font-medium text-gray-400"
                    >
                      {option.nombre}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <FontAwesomeIcon
                    className="h-[2dvh] text-gray-600"
                    icon={faAngleDown}
                  />
                </div>
              </form>
            {/* </ToolTips> */}
          </div>

          {/* Edad */}

          <div className="text-center">
            <label className="font-main font-semibold text-md uppercase">
              Edad
            </label>

            {/* <ToolTips
              text={`0-4 años: Cachorro\n5-8 años: Adulto\n9-15+ años: Anciano`}
            > */}
              <form className="flex item-center justify-center relative h-[60px] min-w-[228px] w-[10dvw] font-main">
                <select
                  name="edad"
                  value={catEdad} // Usa catEdad para mostrar la selección
                  onChange={handleChange}
                  className={`font-main font-medium h-full w-full p-2 pl-4 pr-8 appearance-none bg-white ${getBorderClass(
                    formData.edad
                  )} rounded-lg hover:border-gray-400 focus:ring-1 focus:ring-[#504527] transition-all duration-200 cursor-pointer text-gray-700 shadow-sm`}
                >
                  <option value="" className="font-main font-medium border-b-2">
                    Seleccionar categoría
                  </option>
                  {edadCategories.map((option, index) => (
                    <option
                      key={index}
                      value={option.cat}
                      className="font-main font-medium text-gray-400"
                    >
                      {option.cat}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <FontAwesomeIcon
                    className="h-[2dvh] text-gray-600"
                    icon={faAngleDown}
                  />
                </div>
              </form>
            {/* </ToolTips> */}
          </div>

          {/* Tamaño */}
          <div className="text-center">
            <label className="font-main font-semibold uppercase">Tamaño</label>
            <form className="flex items-center justify-center relative h-[60px] min-w-[228px] w-[10dvw] font-main">
              <select
                name="tamano"
                value={formData.tamano}
                onChange={handleChange}
                className={`font-main z-50 font-medium h-full w-full p-2 pl-4 pr-8 appearance-none bg-white ${getBorderClass(
                  formData.tamano
                )} rounded-lg hover:border-gray-400 focus:ring-1 focus:ring-[#504527] transition-all duration-200 cursor-pointer text-gray-700 shadow-sm`}
              >
                <option
                  value=""
                  className="font-main font-semibold text-md border-b-2"
                >
                  Seleccionar tamaño
                </option>
                {petFormDataState.tamano.map((option, index) => (
                  <option
                    key={index}
                    value={option.id}
                    className="font-main font-semibold text-gray-400"
                  >
                    {option.nombre}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 z-50">
                <FontAwesomeIcon
                  className="h-[2dvh] text-gray-600"
                  icon={faAngleDown}
                />
              </div>
            </form>
          </div>
        </section>
      </div>

      <MapModal
        show={showMapModal}
        onClose={() => {
          // Al cerrar, resetear estados temporales a los valores actuales
          setTempLat(lat);
          setTempLng(lng);
          setTempRadius(radius);
          setShowMapModal(false);
        }}
        onMarkerAdded={handleTempMarkerAdded} // Usa la función temporal que NO filtra
        onSave={async (savedLat, savedLng, savedRadius) => {
          // Obtener información de la ubicación
          const locationInfo = await getLocationInfo(savedLat, savedLng);
          setSelectedLocationInfo(locationInfo);

          // SOLO AQUÍ se actualiza y filtra - cuando se presiona "Guardar"
          setRadius(savedRadius);
          onRadioChange(savedRadius, savedLat, savedLng);
          handleFilter();
        }}
        radius={tempRadius} // Usa valores temporales
        lat={tempLat} // Usa valores temporales
        lng={tempLng} // Usa valores temporales
        onRadiusChange={handleTempRadiusChange} // Usa función temporal
      />
    </>
  );
};

export default Categories;
